<script setup>
import { useElementSize } from '@vueuse/core';
import { computed, onMounted, ref, nextTick, watch } from 'vue';
import HoverCard from '../table/HoverCard.vue';
import PickupCard from '../table/PickupCard.vue';
import ProofUpload from '../table/ProofUpload.vue';
import ListCard from '../table/ListCard.vue';

const container = ref();
const { width, height } = useElementSize(container);

// Define props
const props = defineProps({
  data: {
    type: Array,
    default: () => []
  },
  columns: {
    type: Array,
    default: () => []
  },
  action: {
    type: Function,
    default: null
  }
});

// Define emits
const emit = defineEmits(['edit-row']);

// Function to emit the edit event
function handleRowEdit(rowData) {
  console.log('[TmsTable] Handling row edit with data:', rowData);
  
  // If there's an action prop provided, use it directly
  if (props.action && typeof props.action === 'function') {
    props.action(rowData);
  } else {
    // Otherwise, emit the edit-row event for parent components to handle
    emit('edit-row', rowData);
  }
}

// Debugging to help track data issues
watch(() => props.data, (newVal) => {
  console.log('[TmsTable] Data changed:', newVal);
  console.log('[TmsTable] Data length:', newVal ? newVal.length : 0);
}, { immediate: true, deep: true });

onMounted(() => {
  console.log('[TmsTable] Mounted with data:', props.data);
  console.log('[TmsTable] Columns:', props.columns);
});

const bookingStatus = {
  'In Delivery': '',
  Departed: '',
  'Failed on Delivery': '',
  'Pickup Completed': '',
  'Job Assigned': 'bg-red',
};
const orderType = { shipment: 'bg-orange-2 text-orange-8 font-bold' };
const region = { outstation: 'bg-orange-2 text-orange-8 font-bold' };

const search = ref('');
const pagination = ref({
  rowsPerPage: 25,
  // rowsNumber: xx if getting data from a server
});

onMounted(async () => {
  await nextTick();
  // pagination.value.rowsPerPage = 25;
});

// Fixed visibleColumns to make sure it always has required columns
const visibleColumns = ref([]);

// Initialize visibleColumns from props when component mounts
watch(() => props.columns, (newColumns) => {
  if (newColumns && Array.isArray(newColumns)) {
    visibleColumns.value = newColumns.map(col => col.field);
    
    // Make sure 'actions' column is included
    if (!visibleColumns.value.includes('actions')) {
      visibleColumns.value.push('actions');
    }
  }
}, { immediate: true });

const allSelected = computed(() => {
  if (!props.columns || !Array.isArray(props.columns) || props.columns.length === 0) {
    return false;
  }
  return visibleColumns.value.length >= props.columns.length;
});

function toggleAll() {
  if (allSelected.value) {
    visibleColumns.value = [];
  } else {
    if (props.columns && Array.isArray(props.columns)) {
      visibleColumns.value = props.columns.map(col => col.field);
      
      // Make sure 'actions' is included
      if (!visibleColumns.value.includes('actions')) {
        visibleColumns.value.push('actions');
      }
    }
  }
}

// Status badge styles
const statusBadgeStyles = {
  'created': { label: 'Created', color: 'grey', },
  'in-progress': { label: 'In Progress', color: 'blue', },
  'completed': { label: 'Completed', color: 'green',},
  'partial': { label: 'Partial', color: 'orange', },
  'cancelled': { label: 'Cancelled', color: 'red' }
};

function getStatusStyle(status) {
  if (!status) return { color: 'grey', label: status || 'Unknown' };
  const lowerStatus = status.toLowerCase();
  return statusBadgeStyles[lowerStatus] || { color: 'grey', label: status };
}
</script>

<template>
  <div class="fit flex overflow-hidden p-4">
    <div class="bg- w-full overflow-hidden flex flex-col">
      <div class="flex items-center justify-center gap-2" style="height: 60px">
        <div class="text-xl font-semibold text-primary">
          {{ $attrs.selected }}
        </div>
        <q-btn color="primary" outline icon="refresh" />
        <slot name="header-left" />
        <q-space />
        <q-input
          v-model="search"
          type="text"
          placeholder="Search"
          outlined
          dense
        />
        <q-btn color="primary" label="Columns" outline>
          <q-menu>
            <q-card class="max-w-sm flex flex-col">
              <div class="p-4 flex">
                Toggle Columns
                <q-space />
                <q-checkbox
                  :value="allSelected"
                  :label="allSelected ? 'Clear All' : 'Select All'"
                  @click.native="toggleAll"
                />
              </div>
              <div class="grid grid-cols-3">
                <q-checkbox
                  v-model="visibleColumns"
                  :val="col.field"
                  :label="col.label"
                  v-for="col in columns"
                  :key="col.field"
                />
              </div>
            </q-card>
          </q-menu>
        </q-btn>
      </div>
      
      <div class="w-full flex-1 overflow-hidden" ref="container">
        <q-table
          v-if="width"
          class="my-sticky-header-table"
          :style="{ width: width + 'px', height: height + 'px' }"
          flat
          :data="props.data"
          :columns="props.columns"
          row-key="id"
          :pagination="pagination"
          :visibleColumns="visibleColumns"
          :filter="search"
        >
          <template v-slot:header="props">
            <q-tr :props="props" class="p-0 h-8">
              <q-th
                v-for="col in props.cols"
                :key="col.name"
                :props="props"
                class="py-0"
              >
                {{ col.label }}
              </q-th>
              <q-th>Action</q-th>
            </q-tr>
          </template>
          
          <template v-slot:body="props">
            <q-tr :props="props" class="p-0 h-8">
              <q-td
                v-for="col in props.cols"
                :key="col.name"
                :props="props"
                class="py-0 h-12 font-semibold ellipsis"
              >
                <template v-if="col.name === 'status'">
                  <q-badge 
                    :color="getStatusStyle(props.row.status).color"
                    text-color="white"
                    class="q-px-sm q-py-xs"
                  >
                    {{ getStatusStyle(props.row.status).label }}
                  </q-badge>
                </template>
                <template v-else>
                  {{ props.row[col.field] !== undefined ? props.row[col.field] : '-' }}
                </template>
              </q-td>
              
              <!-- Actions Column -->
              <q-td class="py-0 h-12">
                <div class="row q-gutter-sm">
                  <q-btn flat round dense icon="edit" color="primary" @click="handleRowEdit(props.row)" />
                </div>
              </q-td>
            </q-tr>
          </template>
          
          <template v-slot:no-data>
            <div class="full-width row flex-center q-pa-md text-grey-8">
              <q-icon name="sentiment_dissatisfied" size="2em" class="q-mr-md" />
              <span>No records found</span>
            </div>
          </template>
        </q-table>
      </div>
    </div>
  </div>
</template>

<style lang="sass">
.my-sticky-header-table
  /* height or max-height is important */
  height: 310px

  .q-table__top,
  thead tr:first-child th
    /* bg color is important for th; just specify one */
    background-color: var(--primary)
    color: white

  thead tr th
    position: sticky
    z-index: 1
  thead tr:first-child th
    top: 0

  /* this is when the loading indicator appears */
  &.q-table--loading thead tr:last-child th
    /* height of all previous header rows */
    top: 48px

  /* prevent scrolling behind sticky top row on focus */
  tbody
    /* height of all previous header rows */
    scroll-margin-top: 48px
</style>
